import React from 'react';
import { useAuthQuery as useQuery } from '../../../../../shared/hooks/useAuthQuery';
import { api } from '../../../../../convex/_generated/api';
import { Spinner } from '../../../../../catalyst-components/spinner';
import { HorizontalBarChart } from './HorizontalBarChart';
import { DistributionHistogram } from './DistributionHistogram';
import { InformationCircleIcon } from '@heroicons/react/24/outline';

export const OnboardingTab: React.FC = () => {
    const data = useQuery(api.admin_engagement_analytics.getOnboardingDistributions);

    if (!data) {
        return (
            <div className="flex justify-center items-center h-64">
                <Spinner className="h-12 w-12" />
            </div>
        );
    }

    const reasonData = Object.entries(data.reasonForSearch).map(([label, value]) => ({ label, value }));
    const phaseData = Object.entries(data.irlSearchPhase).map(([label, value]) => ({ label, value }));
    const durationData = Object.entries(data.searchDuration).map(([label, value]) => ({ label, value }));
    const stateData = Object.entries(data.state).map(([label, value]) => ({ label, value }));
    const usersWithState = stateData.reduce((sum, d) => sum + d.value, 0);

    return (
        <div className="space-y-8">
            {/* Info banner */}
            <div className="flex items-start gap-3 p-4 rounded-xl bg-blue-50 border border-blue-200">
                <InformationCircleIcon className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-blue-800">
                    These charts show <strong>current</strong> user profile values. Historical/original onboarding values are not tracked separately.
                    Users may update their profile over time.
                </p>
            </div>

            {/* Onboarding Funnel Summary */}
            <div className="flex flex-wrap items-center gap-3">
                <div className="px-4 py-2 bg-card border-2 border-border/50 rounded-full">
                    <span className="text-sm text-muted-foreground">Total Profiles: </span>
                    <span className="font-semibold text-foreground">{data.totalUsers}</span>
                </div>
                <div className="px-4 py-2 bg-card border-2 border-border/50 rounded-full">
                    <span className="text-sm text-muted-foreground">Onboarding Completed: </span>
                    <span className="font-semibold text-foreground">{data.onboardingCompleted}</span>
                </div>
                <div className="px-4 py-2 bg-card border-2 border-border/50 rounded-full">
                    <span className="text-sm text-muted-foreground">Completion Rate: </span>
                    <span className="font-semibold text-foreground">
                        {data.totalUsers > 0 ? Math.round((data.onboardingCompleted / data.totalUsers) * 100) : 0}%
                    </span>
                </div>
            </div>

            {/* Onboarding Drop-off */}
            {Object.keys(data.stepDropoff).length > 0 && (
                <DistributionHistogram
                    data={data.stepDropoff}
                    title="Onboarding Drop-off by Step"
                    subtitle="Users who stopped onboarding at each step (0=resume, 3=state, 4=reason, 5=phase, 6=duration, 8=readiness)"
                    xAxisLabel="Step Index"
                    barColor="bg-amber-400/60"
                />
            )}

            {/* Reason for Search */}
            <HorizontalBarChart
                data={reasonData}
                title="Reason for Job Search"
                subtitle="Why users are searching for jobs"
            />

            {/* IRL Search Phase */}
            <HorizontalBarChart
                data={phaseData}
                title="Job Search Phase (Current)"
                subtitle="Multi-select: users can appear in multiple phases. This also serves as the search phase migration view."
            />

            {/* Search Duration */}
            <HorizontalBarChart
                data={durationData}
                title="Search Duration"
                subtitle="How long users have been searching"
                sortBy="label"
            />

            {/* Readiness Score */}
            <DistributionHistogram
                data={data.howReady}
                title="Readiness to Start Search"
                subtitle="Self-reported readiness on a 1-10 scale"
                xAxisLabel="Readiness Score"
            />

            {/* State Distribution */}
            <HorizontalBarChart
                data={stateData}
                title="Geographic Distribution"
                subtitle={`${usersWithState} of ${data.totalUsers} users provided location — only users who completed the state onboarding step are shown`}
                maxBars={15}
            />
        </div>
    );
};
